import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { updateCompanyProfileValue, removeTenant, updateIsCompanyRemoving } from '@reducers/membership';
import type { RootState } from '@store/types';
import { Alert, Popup, languages } from '@helpers';

import ChangeFactoryTime from '@components/settings/components/modules/changeFactoryTime';
import ChangeFactoryLanguage from '@components/settings/components/modules/changeFactoryLanguage';
import ChangeFactoryCountry from '@components/settings/components/modules/changeFactoryCountry';

export interface CompanyFieldItem {
  name: string;
  value: string;
  onClick?: () => void;
}

export interface UseManageCompanyReturn {
  companyProfile: Record<string, unknown>;
  isCompanyRemoving: boolean;
  notifyOnMachineErrors: boolean;
  factoryTimeZone: string;
  factoryLanguageDisplay: string;
  notificationLoading: boolean;
  triggerNotification: (value: boolean) => Promise<void>;
  showEditField: boolean;
  editFieldConfig: Record<string, unknown>;
  toggleEditField: () => void;
  openFactoryTimePicker: () => void;
  openFactoryLanguagePicker: () => void;
  openFactoryCountryPicker: () => void;
  companyList: CompanyFieldItem[];
  openProfileFieldEditor: (item: CompanyFieldItem) => void;
  removeAlert: () => void;
}

export const useManageCompany = (): UseManageCompanyReturn => {
  const dispatch = useAppDispatch();

  const userInfo = useSelector((state: RootState) => state.user?.userInfo);
  const isCompanyRemoving = useSelector((state: RootState) => (state as any).membership?.isCompanyRemoving ?? false);

  const companyProfile = userInfo?.companyProfile || {};
  const notifyOnMachineErrors = (companyProfile as any)?.notifyOnMachineErrors ?? false;
  const factoryTimeZone = (companyProfile as any)?.factoryTimeZone ?? '';
  const factoryLanguageDisplay = languages.find((i: any) => i.value === (companyProfile as any)?.language)?.name || '';

  const [notificationLoading, setNotificationLoading] = useState(false);
  const [showEditField, setShowEditField] = useState(false);
  const [editFieldConfig, setEditFieldConfig] = useState<Record<string, unknown>>({});

  const triggerNotification = useCallback(
    async (value: boolean) => {
      setNotificationLoading(true);
      await dispatch(updateCompanyProfileValue({ notifyOnMachineErrors: value }));
      setNotificationLoading(false);
    },
    [dispatch],
  );

  const toggleEditField = useCallback(() => {
    setShowEditField((prev) => !prev);
  }, []);

  const openFactoryTimePicker = useCallback(() => {
    Popup({ Component: ChangeFactoryTime });
  }, []);

  const openFactoryLanguagePicker = useCallback(() => {
    Popup({ Component: ChangeFactoryLanguage });
  }, []);

  const openFactoryCountryPicker = useCallback(() => {
    Popup({ Component: ChangeFactoryCountry });
  }, []);

  const openProfileFieldEditor = useCallback(
    (item: CompanyFieldItem) => {
      setEditFieldConfig({
        header: item.name,
        initData: {
          [item.value]: (companyProfile as any)[item.value],
        },
        onSubmit: async (data: any, onClose: () => void) => {
          await dispatch(updateCompanyProfileValue({ [item.value]: data[item.value] }));
          onClose();
        },
        inputs: [
          {
            key: item.value,
            placeholder: item.name,
            noArrow: true,
            isLable: false,
          },
        ],
      });
      setShowEditField(true);
    },
    [dispatch, companyProfile],
  );

  const companyList: CompanyFieldItem[] = useMemo(
    () => [
      { name: i18n.t('Company Name'), value: 'companyName' },
      { name: i18n.t('Address'), value: 'addressLine1' },
      { name: i18n.t('City'), value: 'city' },
      { name: i18n.t('Postal code'), value: 'postalCode' },
      { name: i18n.t('Country'), value: 'countryName', onClick: openFactoryCountryPicker },
      { name: i18n.t('Phone'), value: 'phoneNumber' },
    ],
    [openFactoryCountryPicker],
  );

  const removeAlert = useCallback(() => {
    Alert({
      title: `${i18n.t('Delete my Company')}?`,
      text: () => null,
      position: 'vertical',
      actions: [
        {
          text: i18n.t('Delete my Company'),
          style: 'danger',
          onPress: async () => {
            const result = await dispatch(removeTenant());
            if (!result || (result as any).isError) {
              return;
            }

            if ((result as any)?.statusCode === 'NotAuthorized') {
              return Alert({ title: (result as any).errorMessage });
            }

            setEditFieldConfig({
              header: 'Authorization Code',
              bottomText: 'Enter the authorization code. This code is provided by email.',
              initData: { code: '' },
              onSubmit: async ({ code }: any, onClose: () => void) => {
                const codeResult = await dispatch(removeTenant(code));
                if ((codeResult as any)?.statusCode === 'TenantRemoveStarted') {
                  onClose();
                  dispatch(updateIsCompanyRemoving(true));
                } else {
                  Alert({ title: i18n.t('Error'), type: 'danger' });
                }
              },
              inputs: [
                {
                  key: 'code',
                  placeholder: 'Authorization Code',
                  noArrow: true,
                  isLable: false,
                },
              ],
            });
            setShowEditField(true);
          },
        },
        {
          text: i18n.t('Cancel'),
          onPress: () => {},
          style: 'primary',
        },
      ],
    });
  }, [dispatch]);

  return {
    companyProfile,
    isCompanyRemoving,
    notifyOnMachineErrors,
    factoryTimeZone,
    factoryLanguageDisplay,
    notificationLoading,
    triggerNotification,
    showEditField,
    editFieldConfig,
    toggleEditField,
    openFactoryTimePicker,
    openFactoryLanguagePicker,
    openFactoryCountryPicker,
    companyList,
    openProfileFieldEditor,
    removeAlert,
  };
};
